import React, { useState, useEffect, useRef } from 'react';
import { 
    Stack, 
    Text, 
    PrimaryButton, 
    DefaultButton,
    MessageBar,
    MessageBarType,
    Spinner,
    SpinnerSize
} from '@fluentui/react';
import { ArrowUploadRegular, ImageRegular, OpenRegular } from '@fluentui/react-icons';
import { authenticatedFetch } from '../utils/authApi';
import { buildMediaUrl } from '../config/apiConfig';

type Props = {
  fieldKey: string; // UI_LOGO or UI_FAVICON
  label: string;
  onSaved?: (url: string) => void;
}

const AdminLogoUploader: React.FC<Props> = ({ fieldKey, label, onSaved }) => {
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [currentUrl, setCurrentUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Try to fetch existing config value for this field
    (async () => {
      try {
        const r = await authenticatedFetch(`/api/admin/config/field/${fieldKey}`);
        if (!r.ok) return;
        const js = await r.json();
        if (js && js.data) setCurrentUrl(js.data.value || null);
      } catch (e) {
        // ignore
      }
    })();
  }, [fieldKey]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    if (e.target.files && e.target.files.length > 0) {
      setFile(e.target.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!file) return;
    setLoading(true);
    setError(null);

    try {
      const fd = new FormData();
      fd.append('file', file);

      // Upload file using authenticated fetch (supports FormData with Bearer token)
      const resp = await authenticatedFetch('/admin/upload_media', { method: 'POST', body: fd });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data?.error || 'Upload failed');

      const url = data.url;

      // Save into admin config via admin API (PUT to config field)
      const saveResp = await authenticatedFetch(`/api/admin/config/field/${fieldKey}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ value: url })
      });

      const saveData = await saveResp.json();
      if (!saveResp.ok || !saveData.success) throw new Error(saveData.error || 'Failed to save config value');

      setCurrentUrl(url);
      setSuccess('Logo uploaded successfully!');
      if (onSaved) onSaved(url);
      // Refresh frontend settings in the client so header/logo updates immediately
      try {
        const fsResp = await authenticatedFetch('/frontend_settings', { method: 'GET' });
        if (fsResp && fsResp.ok) {
          const fsJson = await fsResp.json();
          // Broadcast updated frontend settings so global state can pick it up
          try {
            window.dispatchEvent(new CustomEvent('frontendSettingsUpdated', { detail: fsJson }));
          } catch (e) {
            // ignore
          }
        }
      } catch (e) {
        // ignore
      }
    } catch (err: any) {
      setError(err.message || String(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Stack tokens={{ childrenGap: 12 }} style={{ padding: '12px 0' }}>
      {/* Current image preview */}
      {currentUrl && (
        <Stack 
          horizontal 
          verticalAlign="center" 
          tokens={{ childrenGap: 16 }}
          style={{
            padding: 12,
            backgroundColor: '#f8f9fa',
            borderRadius: 8,
            border: '1px solid #e1e5e9'
          }}
        >
          <img 
            src={buildMediaUrl(currentUrl)} 
            alt={label} 
            style={{ 
              maxWidth: 120, 
              maxHeight: 60, 
              objectFit: 'contain',
              borderRadius: 4,
              backgroundColor: '#fff',
              padding: 4,
              border: '1px solid #e1e5e9'
            }} 
          />
          <Stack tokens={{ childrenGap: 4 }}>
            <Text style={{ fontSize: 12, color: '#666' }}>Current {label.toLowerCase()}</Text>
            <a 
              href={buildMediaUrl(currentUrl)} 
              target="_blank" 
              rel="noreferrer"
              style={{ 
                fontSize: 12, 
                color: '#667eea', 
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: 4
              }}
            >
              <OpenRegular style={{ fontSize: 12 }} />
              View full size
            </a>
          </Stack>
        </Stack>
      )}
      
      {/* Upload section */}
      <Stack horizontal tokens={{ childrenGap: 12 }} verticalAlign="center">
        <input
          type="file"
          ref={fileInputRef}
          accept="image/*"
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />
        <DefaultButton
          iconProps={{ iconName: 'Attach' }}
          text={file ? file.name : 'Choose File'}
          onClick={() => fileInputRef.current?.click()}
          styles={{
            root: {
              borderRadius: 6,
              minWidth: 140,
              borderStyle: 'dashed',
              borderColor: file ? '#667eea' : '#c7d2db'
            },
            rootHovered: {
              borderColor: '#667eea'
            }
          }}
        />
        <PrimaryButton
          iconProps={{ iconName: 'Upload' }}
          text={loading ? 'Uploading...' : 'Upload'}
          onClick={handleUpload}
          disabled={!file || loading}
          styles={{
            root: { borderRadius: 6, minWidth: 100 }
          }}
        />
        {loading && <Spinner size={SpinnerSize.small} />}
      </Stack>
      
      {/* Status messages */}
      {error && (
        <MessageBar 
          messageBarType={MessageBarType.error}
          onDismiss={() => setError(null)}
          dismissButtonAriaLabel="Close"
        >
          {error}
        </MessageBar>
      )}
      {success && (
        <MessageBar 
          messageBarType={MessageBarType.success}
          onDismiss={() => setSuccess(null)}
          dismissButtonAriaLabel="Close"
        >
          {success}
        </MessageBar>
      )}
    </Stack>
  );
};

export default AdminLogoUploader;
